import { round } from 'lodash'
import {
  HourSerieCalculatorResult,
  HourSeriesResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  ElectricityConsumptionParameters,
  EnergySystemFragment,
  EnergySystemType,
  HourSeriesType,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { hoursInYear } from '../util/hours-in-year'
import { calcVentilationHourSeries } from './ventilation-hour-series-calculator'
import { calcHeatLoadHourSeries } from './heatload-hour-series-calculator'
import {
  CalibrationHourSeriesResult,
  CalibrationHourSeriesType,
} from '../components/building-energy-models/calibration/calibration-util'
import { i18n } from '../i18n'

export const electricityConsumptionCalculator = (
  ventilationElectricitySerie: number[],
  appliancesElectricitySerie: number[],
  lightingElectricitySerie: number[],
  energySystem: EnergySystemFragment,
  year: number,
): HourSerieCalculatorResult => {
  const params = energySystem.parameters as ElectricityConsumptionParameters

  const baseLoadSchedule = energySystem.schedule ?? undefined
  if (!baseLoadSchedule) throw new Error('Schedule for base load not found')

  const occupantSerie = calculateHourSerieFactors(baseLoadSchedule, year)
  const propertySerie = Array.from(Array(hoursInYear).fill(0)) as number[]

  for (let i = 0; i < occupantSerie.length; i++) {
    const currBaseLoad = (params.electricityBaseLoad * occupantSerie[i]) / 1000

    // Set total consumption first to the property serie
    propertySerie[i] =
      round(
        currBaseLoad +
          ventilationElectricitySerie[i] +
          appliancesElectricitySerie[i] +
          lightingElectricitySerie[i],
        2,
      ) * 1000

    occupantSerie[i] =
      round(
        currBaseLoad * params.occupantBaseLoadElShare +
          ventilationElectricitySerie[i] * params.occupantVentilationElShare +
          appliancesElectricitySerie[i] * params.occupantApplianceElShare +
          lightingElectricitySerie[i] * params.occupantLightingElShare,
        2,
      ) * 1000

    propertySerie[i] -= occupantSerie[i]
  }

  const result: HourSerieCalculatorResult = {
    outputs: [
      {
        hourSerieType: HourSeriesType.ElectricityDemand,
        energySystemType: EnergySystemType.ElectricityConsumption,
        consumptionEnergyType: ConsumptionEnergyType.OccupantsElectricity,
        hourSerieName: i18n.t(
          'buildingEnergyModels.hourSeriesNames.elOccupants',
        ),
        hourSerie: occupantSerie,
      },
      {
        hourSerieType: HourSeriesType.ElectricityDemand,
        energySystemType: EnergySystemType.ElectricityConsumption,
        consumptionEnergyType: ConsumptionEnergyType.PropertyElectricity,
        hourSerieName: i18n.t(
          'buildingEnergyModels.hourSeriesNames.elProperty',
        ),
        hourSerie: propertySerie,
      },
    ],
    inputs: [],
  }

  return result
}

export const calcElectricityConsumtionHourSeries = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  area: number,
  year: number,
): CalibrationHourSeriesResult[] => {
  const elConsumtion = model.energySystems.find(
    (s) => s.type === EnergySystemType.ElectricityConsumption,
  )

  if (!elConsumtion) return []
  if (!elConsumtion.schedule) return []

  const params = elConsumtion.parameters as ElectricityConsumptionParameters

  const baseLoadFactors = calculateHourSerieFactors(elConsumtion.schedule, year)
  const baseLoadSerie = Array<number>(hoursInYear).fill(0)

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ventilationSerie =
    ventilationResult.inputs.find(
      (r) =>
        r.inputSerieType === InputHourSerieType.VentilationElectricityDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const heatloadResult = calcHeatLoadHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const appliancesSerie =
    heatloadResult.inputs.find(
      (r) =>
        r.inputSerieType ===
        InputHourSerieType.HeatLoadAppliancesElectricityDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const lightingSerie =
    heatloadResult.inputs.find(
      (r) =>
        r.inputSerieType ===
        InputHourSerieType.HeatLoadLightingElectricityDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  for (let i = 0; i < baseLoadSerie.length; i++) {
    baseLoadSerie[i] = params.electricityBaseLoad * baseLoadFactors[i]
    ventilationSerie[i] *= 1000
    appliancesSerie[i] *= 1000
    lightingSerie[i] *= 1000
  }

  return [
    {
      type: CalibrationHourSeriesType.ElectricityBaseLoad,
      serie: baseLoadSerie,
    },
    {
      type: CalibrationHourSeriesType.ElectricityVentilation,
      serie: ventilationSerie,
    },
    {
      type: CalibrationHourSeriesType.ElectricityLighting,
      serie: lightingSerie,
    },
    {
      type: CalibrationHourSeriesType.ElectricityAppliances,
      serie: appliancesSerie,
    },
  ]
}

export const calcNetElectricityConsumtionDensityHourSerie = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  area: number,
  year: number,
): number[] => {
  const result = Array<number>(hoursInYear).fill(0)

  const series = calcElectricityConsumtionHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  if (series.length === 0) return result

  for (let i = 0; i < result.length; i++) {
    for (let j = 0; j < series.length; j++) {
      result[i] += series[j].serie[i]
    }
  }

  const electricitySerie = result.map((i) => round(i / area, 3))

  return electricitySerie
}

const getConsumptionEnergyType = (
  type: CalibrationHourSeriesType,
): ConsumptionEnergyType => {
  switch (type) {
    case CalibrationHourSeriesType.ElectricityBaseLoad:
      return ConsumptionEnergyType.BaseLoadElectricity
    case CalibrationHourSeriesType.ElectricityVentilation:
      return ConsumptionEnergyType.VentilationElectricity
    case CalibrationHourSeriesType.ElectricityLighting:
      return ConsumptionEnergyType.LightingElectricity
    case CalibrationHourSeriesType.ElectricityAppliances:
      return ConsumptionEnergyType.AppliancesElectricity
    default:
      return ConsumptionEnergyType.PropertyElectricity
  }
}

const getHourSerieName = (type: CalibrationHourSeriesType): string => {
  switch (type) {
    case CalibrationHourSeriesType.ElectricityBaseLoad:
      return i18n.t('buildingEnergyModels.hourSeriesNames.elBaseLoad')
    case CalibrationHourSeriesType.ElectricityVentilation:
      return i18n.t('buildingEnergyModels.hourSeriesNames.elVentilation')
    case CalibrationHourSeriesType.ElectricityLighting:
      return i18n.t('buildingEnergyModels.hourSeriesNames.elLighting')
    case CalibrationHourSeriesType.ElectricityAppliances:
      return i18n.t('buildingEnergyModels.hourSeriesNames.elAppliances')
    default:
      return ''
  }
}

export const calcElectricityHourSeries = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  area: number,
  year: number,
): HourSeriesResult[] => {
  const calibrationResult = calcElectricityConsumtionHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const result: HourSeriesResult[] = []

  calibrationResult.forEach((r) => {
    result.push({
      hourSerieType: HourSeriesType.ElectricityDemand,
      energySystemType: EnergySystemType.ElectricityConsumption,
      consumptionEnergyType: getConsumptionEnergyType(r.type),
      hourSerieName: getHourSerieName(r.type),
      hourSerie: r.serie,
    })
  })

  return result
}

export const calcSumElectricityHourSeries = (
  model: BuildingEnergyModelFragment,
  baseLoadElectricitySerie: number[],
  ventilationElectricitySerie: number[],
  appliancesElectricitySerie: number[],
  lightingElectricitySerie: number[],
): HourSeriesResult[] => {
  const occupantSerie = Array<number>(hoursInYear).fill(0)
  const propertySerie = Array<number>(hoursInYear).fill(0)

  const elConsumtion = model.energySystems.find(
    (s) => s.type === EnergySystemType.ElectricityConsumption,
  )

  if (!elConsumtion) return []

  const params = elConsumtion.parameters as ElectricityConsumptionParameters

  for (let i = 0; i < baseLoadElectricitySerie.length; i++) {
    const currBaseLoad = baseLoadElectricitySerie[i]

    // Set total consumption first to the property serie
    propertySerie[i] =
      currBaseLoad +
      ventilationElectricitySerie[i] +
      appliancesElectricitySerie[i] +
      lightingElectricitySerie[i]

    occupantSerie[i] =
      currBaseLoad * params.occupantBaseLoadElShare +
      ventilationElectricitySerie[i] * params.occupantVentilationElShare +
      appliancesElectricitySerie[i] * params.occupantApplianceElShare +
      lightingElectricitySerie[i] * params.occupantLightingElShare

    propertySerie[i] -= occupantSerie[i]
  }

  return [
    {
      hourSerieType: HourSeriesType.ElectricityDemand,
      energySystemType: EnergySystemType.ElectricityConsumption,
      consumptionEnergyType: ConsumptionEnergyType.OccupantsElectricity,
      hourSerieName: i18n.t('buildingEnergyModels.hourSeriesNames.elOccupants'),
      hourSerie: occupantSerie,
    },
    {
      hourSerieType: HourSeriesType.ElectricityDemand,
      energySystemType: EnergySystemType.ElectricityConsumption,
      consumptionEnergyType: ConsumptionEnergyType.PropertyElectricity,
      hourSerieName: i18n.t('buildingEnergyModels.hourSeriesNames.elProperty'),
      hourSerie: propertySerie,
    },
  ]
}
